import type {
  Circuit,
  CircuitFreeDecor,
  CircuitRoadPiece,
  TrackCell,
} from '@multirx/shared/types/track.js';
import { TrackCellType } from '@multirx/shared/types/track.js';
import {
  countCellTypes,
  countPathSegmentTypes,
  finalizeEditedCircuit,
  findPathIndexAt,
  getCircuitEditErrorCells,
  getCorePath,
  isPathClosed,
  validateManualCircuitEdit,
} from '@multirx/shared/game/circuitEdit.js';
import {
  addRoadPiece,
  canGroupRoadPieceRun,
  coveredOffsets,
  findRoadPieceAt,
  pruneRoadPieces,
  removeRoadPiece,
  resolveRoadPieceCells,
} from '@multirx/shared/game/circuitRoadPieceEdit.js';
import { findDecor3dAt, removeDecor3d } from '@multirx/shared/game/circuitPropEdit.js';
import {
  getCircuitCellStyle,
  setCircuitCellFill,
  type CircuitSurface,
} from '@multirx/shared/game/circuitSurfaceStyle.js';
import {
  addFreeDecor,
  findFreeDecorNear,
  moveFreeDecor,
  removeFreeDecor,
  rotateFreeDecor,
  setFreeDecorElevation,
} from '@multirx/shared/game/circuitFreeDecorEdit.js';
import {
  areCellsAdjacent,
  getPlaceableDrawCells,
  tryDrawPathCell,
  undoLastPathCell,
} from '@multirx/shared/game/circuitGeometryEdit.js';
import {
  clearJokerLap,
  getJokerLapPlaceableCells,
  setJokerLapEntry,
  setJokerLapExit,
  tryAppendJokerLapCell,
  undoLastJokerLapCell,
} from '@multirx/shared/game/circuitJokerLapEdit.js';
import {
  clearStartLane,
  getStartLanePlaceableCells,
  setStartLaneJoin,
  tryAppendStartLaneCell,
  undoLastStartLaneCell,
} from '@multirx/shared/game/circuitStartLaneEdit.js';
import { START_LANE_LENGTH } from '@multirx/shared/types/track.js';
import type { CircuitPiece3dCatalogEntry } from '@multirx/shared/game/circuitPieceCatalog.js';
import { createPillButton } from '../ui/pillButton.js';
import { openCircuitPiecePicker } from '../ui/circuitPiecePicker.js';
import { buildTrack3dTiles, tileStepToDirection } from './track3dLayout.js';
import { proceduralCornerSlug } from './proceduralRoadPieces.js';
import { getDecorModelInfo, type DecorModelInfo } from './circuit3dTrackScene.js';
import {
  ERROR_HIGHLIGHT_COLOR,
  HOVER_HIGHLIGHT_COLOR,
  mountCircuit3dEditorScene,
  type Circuit3dEditorSceneHandle,
  type GhostModelPreview,
  type GridCell,
  type GroundPoint,
  type HighlightCell,
} from './circuit3dEditorScene.js';
import { saveCircuitCopy, updateCircuit, type SavedCircuit } from './api.js';
import type { CircuitEditorSource } from './circuitEditor.js';

export interface Circuit3dEditorHandle {
  dispose(): void;
}

/** Met à jour le libellé (statique + hover) d'un bouton pill créé par `createPillButton`. */
function setPillButtonLabel(button: HTMLElement, label: string): void {
  button.querySelectorAll('.btn-pill-inner-static, .btn-pill-inner-hover').forEach((el) => {
    el.textContent = label;
  });
}

/** (Dés)active un bouton pill créé par `createPillButton` — typé `HTMLElement`
 * (peut être un `<a>`), donc pas de propriété `.disabled` native utilisable
 * directement ; l'attribut suffit, le style grisé vient de `.btn-pill:disabled`
 * dans `btn-pill.css`. */
function setButtonDisabled(button: HTMLElement, disabled: boolean): void {
  if (disabled) button.setAttribute('disabled', 'true');
  else button.removeAttribute('disabled');
}

/** Nombre de cases occupées par une pièce du catalogue (1 à 3 en ligne droite, 1/3/5 en virage). */
function pieceCellCost(piece: CircuitPiece3dCatalogEntry): number {
  return piece.cellCost;
}

/**
 * Éditeur de circuit 3D — pas de bascule de "mode" manuelle : la phase active
 * se déduit entièrement de l'état du brouillon (voir `isTrackReady`), et le
 * clic droit sert d'annulation/suppression universelle (voir `handleRightClick`) :
 * - Tant que le tracé n'est pas prêt : construit le tracé depuis une grille
 *   vide (première case = départ, cases suivantes adjacentes, clic sur le
 *   départ pour fermer la boucle) — réutilise telle quelle la logique
 *   partagée de l'éditeur 2D (`circuitGeometryEdit.ts`), aucune duplication de
 *   règles. Une fois la boucle fermée, enchaîne automatiquement sur le tracé
 *   de la ligne de départ (`circuitStartLaneEdit.ts`, 5 cases exactement,
 *   alignées, partant du départ) — optionnelle (bouton "Passer" pour
 *   l'ignorer), mais recommandée : sans elle, tous les pilotes démarrent sur
 *   la même case du tracé principal. Clic droit = annule la dernière case
 *   posée, n'importe où.
 * - Une fois le tracé prêt (`isTrackReady`) : pièces route (M2/M3, 1 à 3 cases
 *   Small/Large/Larger) sur une case droite/virage déjà tracée — rotation
 *   toujours dérivée de la direction existante, jamais un choix libre (une
 *   pièce ne fait que reskinner une case déjà tracée, voir
 *   `Circuit.roadPieces`) — et décor (M4, tribunes/tentes/panneaux/etc., y
 *   compris tout modèle voiture) hors tracé, en **placement libre** : position
 *   flottante exacte sous le curseur et rotation libre au clic molette +
 *   glisser (voir `onMiddleDragRotate`), sans empreinte de cases ni
 *   vérification d'occupation —
 *   plusieurs décors peuvent se superposer à volonté (voir
 *   `shared/game/circuitFreeDecorEdit.ts`). Clic droit = retire la pièce/le
 *   décor sous le curseur. Un bouton de secours ("↶ Reprendre le tracé")
 *   permet malgré tout de rouvrir la boucle si besoin.
 */
export function mountCircuit3dEditor(
  container: HTMLElement,
  saved: CircuitEditorSource,
  onSaved?: (saved: SavedCircuit) => void
): Circuit3dEditorHandle {
  container.innerHTML = '';

  let draft: Circuit = structuredClone(saved.circuit);
  (window as unknown as { __debugDraft?: () => Circuit }).__debugDraft = () => draft;
  /** null tant que le circuit n'a jamais été enregistré (brouillon "Nouveau circuit 3D"). */
  let currentId: number | null = saved.isNew ? null : saved.id;
  let selectedPiece: CircuitPiece3dCatalogEntry | null = null;
  /** Rotation libre (0-359°) du prochain décor libre posé — sans effet sur les pièces route. */
  let decorRotationDeg = 0;
  /** Décor libre sélectionné (surligné) — le clic suivant le déplace à cet endroit. */
  let selectedFreeDecorId: string | null = null;
  /** Rayon de détection (unités de case) pour sélectionner/retirer un décor libre au clic. */
  const FREE_DECOR_HIT_RADIUS = 0.5;
  /** « Coller » (voir `computeDecorSnap`) : pose/déplace un décor libre accolé pile
   * contre le bord du décor le plus proche plutôt qu'au point flottant brut. */
  let decorSnapEnabled = false;
  /** Rayon de recherche (unités de case) du voisin à accoler — plus large que
   * `FREE_DECOR_HIT_RADIUS` (sélection au clic) : le collage doit se déclencher
   * dès qu'on approche un voisin, pas seulement en survolant son centre exact. */
  const DECOR_SNAP_SEARCH_RADIUS = 2.5;
  /** URL résolue + gabarit réel des modèles de décor déjà consultés une fois
   * (voir `ensureDecorModelInfo`) — nécessaire au calcul géométrique du collage
   * ET à l'aperçu fantôme d'un décor déjà posé (dont on n'a que le `modelId`,
   * pas l'URL/le gabarit résolus comme pour `selectedPiece`). */
  const decorModelInfoCache = new Map<string, DecorModelInfo>();

  /** Résout et met en cache l'URL/le gabarit d'un modèle de décor si ce n'est pas
   * déjà fait — fire-and-forget : le collage reste simplement inactif jusqu'à ce
   * que l'information soit connue (rafraîchit l'aperçu fantôme une fois prête).
   * Appelé dès qu'un décor devient candidat au collage (sélection dans le
   * catalogue, sélection d'un décor déjà posé) pour qu'elle soit déjà en cache
   * au moment où l'utilisateur clique réellement. */
  function ensureDecorModelInfo(slug: string): void {
    if (decorModelInfoCache.has(slug)) return;
    void getDecorModelInfo(slug)
      .then((info) => {
        decorModelInfoCache.set(slug, info);
        refreshGhostModelPreview();
      })
      .catch(() => {
        /* Gabarit inconnu : le collage reste inactif pour ce modèle (best-effort). */
      });
  }
  /** Outil dédié : entrée → branchement (cases hors tracé) → sortie sur le
   * tracé, mêmes trois étapes que l'éditeur 2D via la même API partagée
   * (voir `handleJokerLapToolClick`). Exclusif avec `selectedPiece`/
   * `selectedFreeDecorId`. */
  let jokerLapToolActive = false;
  /** Outil de peinture de surface (voir `setCircuitCellFill`) — exclusif avec
   * `selectedPiece`/`selectedFreeDecorId`/`jokerLapToolActive`, comme les autres
   * outils de clic ci-dessus. Purement esthétique pour l'instant : seule la
   * couleur de la chaussée change au rendu (`circuit3dTrackScene.ts`). */
  let surfaceBrush: CircuitSurface | null = null;
  let hoveredCell: GridCell | null = null;
  /** Dernier point flottant survolé (voir `onHoverPoint`) — permet de recalculer
   * immédiatement l'aperçu fantôme (`refreshGhostModelPreview`) après un
   * changement d'état qui ne vient pas d'un mouvement de souris (sélection
   * d'une pièce, bascule de mode/outil…), sans attendre le prochain survol. */
  let lastHoverPoint: GroundPoint | null = null;
  let actionStatus = '';
  let actionIsError = false;

  /** Taille de virage (nombre de cases de bras, voir `buildProceduralCorner`)
   * → étiquette et couleur de surlignage dans la grille. */
  const ARM_LABELS: Record<1 | 2 | 3, string> = { 1: 'Petit', 2: 'Moyen', 3: 'Large' };
  const ARM_COLORS: Record<1 | 2 | 3, number> = { 1: 0x2ecc71, 2: 0xffd400, 3: 0xe53935 };
  /** Violet pour les cases candidates au branchement (entrée ou continuation),
   * doré (même teinte que la fermeture de boucle en mode Tracer) pour les
   * cases du tracé principal qui referment le tour joker si on clique dessus. */
  const JOKER_LAP_BRANCH_COLOR = 0x9b59b6;
  const JOKER_LAP_EXIT_COLOR = HOVER_HIGHLIGHT_COLOR;

  /** Vrai tant que la ligne de départ (5 cases, voir `circuitStartLaneEdit.ts`)
   * n'est pas encore complète — phase distincte du tracé de la boucle
   * elle-même, mais toujours pilotée en mode Tracer. Se déroule EN PREMIER,
   * juste après la case de départ (voir `handleTraceClick`), pas après la
   * fermeture de la boucle — le point de jonction (`joinPathIndex`) ne dépend
   * pas de la fermeture, seulement de l'existence de la case de départ. */
  function isDrawingStartLane(): boolean {
    return !!draft.startLane && draft.startLane.cells.length < START_LANE_LENGTH;
  }

  /** Vrai une fois le tracé principal fermé ET la ligne de départ résolue
   * (terminée ou passée) — seul moment où décor/pièces/tour joker sont
   * utilisables. Avant ça, l'éditeur est toujours en train de tracer (boucle,
   * puis ligne de départ) : plus de bouton de mode pour en sortir "trop tôt". */
  function isTrackReady(): boolean {
    return isPathClosed(draft.path) && !isDrawingStartLane();
  }

  const root = document.createElement('div');
  root.className = 'circuit-3d-editor-root';
  root.innerHTML = `
    <p class="circuit-3d-editor-instructions" role="status"></p>
    <p class="circuit-3d-hint circuit-3d-editor-validation-status" role="status"></p>
    <div class="circuit-3d-viewport circuit-3d-editor-viewport">
      <div class="circuit-3d-editor-bar-dock">
        <div class="circuit-3d-editor-bar"></div>
      </div>
    </div>
    <div class="circuit-3d-editor-trace-tools"></div>
    <p class="circuit-3d-hint circuit-3d-editor-status" role="status"></p>
    <p class="circuit-3d-hint circuit-3d-editor-action-status" role="status"></p>
  `;
  container.appendChild(root);

  const toolbar = root.querySelector('.circuit-3d-editor-bar') as HTMLElement;
  const barDock = root.querySelector('.circuit-3d-editor-bar-dock') as HTMLElement;
  const instructionsEl = root.querySelector('.circuit-3d-editor-instructions') as HTMLElement;
  const viewport = root.querySelector('.circuit-3d-editor-viewport') as HTMLElement;
  const traceToolsEl = root.querySelector('.circuit-3d-editor-trace-tools') as HTMLElement;

  /** Position haut/bas de la barre flottante, mémorisée comme le panneau
   * "Session" gauche/droite de `TrackGeneratorUI.ts` (même idiome : une classe
   * CSS sur le conteneur, un état persisté en `sessionStorage`, pas de
   * glisser-déposer libre). */
  const BAR_POSITION_KEY = 'multirx-circuit-3d-editor-bar-position';
  let barOnTop = sessionStorage.getItem(BAR_POSITION_KEY) === 'top';
  function applyBarPosition(): void {
    barDock.classList.toggle('circuit-3d-editor-bar-dock--top', barOnTop);
  }
  applyBarPosition();
  const validationStatusEl = root.querySelector(
    '.circuit-3d-editor-validation-status'
  ) as HTMLElement;
  const hoverStatusEl = root.querySelector('.circuit-3d-editor-status') as HTMLElement;
  const actionStatusEl = root.querySelector('.circuit-3d-editor-action-status') as HTMLElement;

  /** Bandeau principal, toujours visible : dit en langage clair quoi cliquer
   * ensuite, en fonction de l'état actuel — la question exacte ("je comprends
   * pas comment utiliser l'éditeur") que ce bandeau répond. Plus de "mode" à
   * afficher : la phase (tracé vs décoration) se déduit de `isTrackReady()`. */
  function updateInstructions(): void {
    let text: string;
    if (!isTrackReady()) {
      if (draft.path.length === 0) {
        text = '① Cliquez n’importe où sur la grille pour poser la case de départ du circuit.';
      } else if (isDrawingStartLane()) {
        const remaining = START_LANE_LENGTH - (draft.startLane?.cells.length ?? 0);
        text = `② Tracez la ligne de départ (case verte à côté du départ) — encore ${remaining} case(s) sur ${START_LANE_LENGTH}, ou « Passer » pour s'en passer. Clic droit pour annuler la dernière case.`;
      } else {
        text =
          '③ Cliquez une case verte pour continuer tout droit ou virage serré (Petit), jaune pour un virage Moyen, rouge pour un virage Large, ou la case dorée du départ pour fermer la boucle. Clic droit pour annuler la dernière case.';
      }
    } else if (jokerLapToolActive) {
      const jl = draft.jokerLap;
      if (!jl || jl.entryPathIndex < 0) {
        text = 'Cliquez une case du circuit principal pour définir l’entrée du tour joker.';
      } else if (jl.exitPathIndex < 0) {
        text =
          'Tracez le passage alternatif (cases hors circuit, en violet), puis cliquez une case dorée du circuit principal pour refermer. Clic droit pour annuler la dernière case du branchement.';
      } else {
        text =
          'Tour joker prêt — chaque pilote devra l’emprunter une fois pendant la course. « Effacer tour joker » pour recommencer.';
      }
    } else if (surfaceBrush) {
      text = `Cliquez une ligne droite ou un virage du tracé principal pour le peindre en ${surfaceBrush === 'earth' ? 'terre' : 'bitume'} — un second clic dessus l'efface.`;
    } else if (selectedFreeDecorId) {
      text =
        'Décor sélectionné — cliquez ailleurs pour le déplacer à cet endroit exact, maintenez le clic molette et déplacez la souris pour le faire pivoter, ou Maj + clic gauche et déplacez la souris pour le monter/descendre.';
    } else if (!selectedPiece) {
      text =
        'Choisissez « Décor / Pièce » ci-dessous, puis cliquez une case pour la poser. Clic droit sur une pièce ou un décor existant pour le retirer.';
    } else if (selectedPiece.category === 'decor') {
      text = `Cliquez n'importe où (placement libre, pas aligné sur la grille) pour poser « ${selectedPiece.displayName} » — maintenez le clic molette et déplacez la souris pour l'orienter avant de cliquer.`;
    } else if (selectedPiece.roadShape === 'corner') {
      const cost = pieceCellCost(selectedPiece);
      text =
        cost > 1
          ? `Cliquez un virage entouré d'au moins ${(cost - 1) / 2} case(s) droite(s) de chaque côté pour poser « ${selectedPiece.displayName} ».`
          : `Cliquez un virage du tracé pour poser « ${selectedPiece.displayName} ».`;
    } else {
      const cost = pieceCellCost(selectedPiece);
      text =
        cost > 1
          ? `Cliquez le début d'une ligne droite d'au moins ${cost} cases pour poser « ${selectedPiece.displayName} ».`
          : `Cliquez une ligne droite du tracé pour poser « ${selectedPiece.displayName} ».`;
    }
    instructionsEl.textContent = text;
  }

  /** Bandeau persistant (connectivité/boucle/départ) — mis à jour après chaque
   * mutation, indépendamment des messages transitoires de `actionStatus`. */
  function refreshValidation(): void {
    const validation = validateManualCircuitEdit(draft);
    const counts = countCellTypes(draft.path);
    const parts = [
      validation.isValid ? 'Circuit valide ✓' : 'Circuit invalide ✗',
      `Départ : ${counts.starts} ${counts.starts === 1 ? '✓' : '✗'}`,
      ...validation.errors,
    ];
    validationStatusEl.textContent = parts.join(' — ');
    validationStatusEl.classList.toggle('circuit-3d-hint--error', !validation.isValid);
    // Masqué en mode Tracer : `getCircuitEditErrorCells` signale en permanence
    // l'extrémité libre d'un tracé pas encore fermé (déjà dit par le bandeau
    // d'instructions et la ligne de validation ci-dessus) — un petit carré rouge
    // juste à côté du surlignage jaune "case suivante" n'apporte rien et se lit
    // comme un bug (surtout à côté d'un virage large, dont l'empreinte visuelle
    // dépasse la case flaggée). Utile en revanche en mode Personnaliser, une fois
    // le tracé figé, pour repérer un chevauchement ou virage devenu incohérent.
    scene?.setErrorCells(isTrackReady() ? getCircuitEditErrorCells(draft) : []);
  }

  function renderHoverStatus(): void {
    hoverStatusEl.textContent = hoveredCell
      ? `Case survolée : (${hoveredCell.x}, ${hoveredCell.y})`
      : 'Survolez le tracé pour cibler une case — glisser pour tourner, molette pour zoomer.';
  }

  /** Retour transitoire (dernière action / erreur) — la consigne "quoi faire
   * maintenant" vit dans `instructionsEl`, ce bandeau ne répète plus le contexte. */
  function renderActionStatus(): void {
    actionStatusEl.textContent = actionStatus;
    actionStatusEl.classList.toggle('circuit-3d-hint--error', actionIsError);
    actionStatusEl.hidden = !actionStatus;
  }

  renderHoverStatus();
  renderActionStatus();
  updateInstructions();

  /** Empreinte à surligner pour une case survolée : la case seule, ou les N cases
   * qu'occuperait la pièce multi-case sélectionnée à partir de cette case (dans le
   * sens du tracé pour une pièce route, ou selon l'emprise fixe + rotation choisie
   * pour un décor) — juste un aperçu, pas une validation (le clic revalide tout). */
  function computeHoverFootprint(cell: GridCell): GridCell[] {
    if (!selectedPiece) return [cell];
    // Décor : placement libre désormais (voir `handleDecorClick`), plus d'empreinte
    // figée à faire pivoter — la case survolée reste juste une approximation visuelle,
    // le clic pose au point flottant exact sous le curseur.
    if (selectedPiece.category === 'decor') {
      return [cell];
    }
    const cellCost = pieceCellCost(selectedPiece);
    if (cellCost <= 1) return [cell];
    const pathIndex = findPathIndexAt(draft, cell.x, cell.y);
    if (pathIndex === null) return [cell];
    const preview: CircuitRoadPiece = {
      id: '',
      source: 'path',
      startIndex: pathIndex,
      cellCost,
      modelId: selectedPiece.slug,
      rotation: 0,
      shape: selectedPiece.roadShape ?? 'straight',
    };
    const resolved = resolveRoadPieceCells(draft, preview);
    return resolved ? resolved.map((c) => ({ x: c.x, y: c.y })) : [cell];
  }

  /** Une case colorée cliquable en mode Tracer : `cells` liste, dans l'ordre,
   * TOUTES les cases de tracé que posera ce clic (1 pour tout droit/fermeture,
   * `armCells + 1` pour un virage — la case de virage elle-même + son bras).
   * `pivot`/`rotationSteps` (virages seulement) permettent d'afficher un aperçu
   * fantôme 3D exact au survol (voir `onHoverCell`), sans deviner la rotation. */
  interface TraceCandidate extends HighlightCell {
    color: number;
    cells: GridCell[];
    armCells?: 1 | 2 | 3;
    pivot?: GridCell;
    rotationSteps?: number;
  }

  /**
   * Simule l'ajout de `cells` (dans l'ordre) sur `draft` sans le modifier — la
   * case de virage elle-même (bras arrière déjà tracé, direction toujours
   * valide) PUIS son bras avant, case par case. `null` si une étape échoue,
   * ou si la boucle se ferme avant la dernière case (fermeture au milieu d'un
   * bras : impossible à représenter, on rejette tout le candidat).
   */
  function simulateAppend(cells: GridCell[]): Circuit | null {
    let scratch = draft;
    for (let i = 0; i < cells.length; i++) {
      const result = tryDrawPathCell(scratch, cells[i].x, cells[i].y);
      if ('error' in result) return null;
      if (result.closed && i !== cells.length - 1) return null;
      scratch = result.circuit;
    }
    return scratch;
  }

  /**
   * Un virage d'une taille donnée dans une direction perpendiculaire donnée,
   * ou `null` s'il est géométriquement impossible dès maintenant (chevauche
   * une pièce déjà posée) : on ne l'affiche alors simplement PAS dans la
   * grille, plutôt que de laisser l'utilisateur cliquer puis échouer sans
   * prévenir (le problème exact signalé en session : « ça fait des grands
   * virages, parfois non, sans alerte »). Les deux sens (gauche/droite) sont
   * toujours proposés — la géométrie procédurale (voir `buildProceduralCorner`)
   * est tangente par construction à ses deux bras, donc un enchaînement de
   * virages de sens opposés (chicane) n'a plus la contrainte des anciens
   * modèles Kenney (sans section droite, qui créait un vrai vide à leur
   * jonction — voir l'ancien `opposesNearestGroupedCorner`, retiré).
   *
   * `last` (déjà dans le tracé) est le pivot du virage — pas une case
   * supplémentaire à ajouter avant de tourner : `computeTile` (voir
   * `track3dLayout.ts`) classe une case en virage dès que sa direction
   * entrante diffère de sa direction sortante, et l'entrante de `last` est
   * déjà figée par les cases précédentes. Le bras (`cells`) part donc
   * directement de `last`, dans `perp` — un virage « Petit » (armCells=1)
   * touche ainsi la case adjacente à la ligne droite en cours, pas une case
   * plus loin.
   */
  function computeCornerCandidate(
    last: GridCell,
    perp: GridCell,
    armCells: 1 | 2 | 3
  ): TraceCandidate | null {
    const cells: GridCell[] = [];
    for (let i = 1; i <= armCells; i++) {
      cells.push({ x: last.x + perp.x * i, y: last.y + perp.y * i });
    }
    const scratch = simulateAppend(cells);
    if (!scratch) return null;

    const pivotIndex = draft.path.length - 1; // `last` lui-même, déjà dans draft.path
    const cellCost = (armCells * 2 + 1) as 3 | 5 | 7;
    if (!canGroupRoadPieceRun(scratch, 'path', pivotIndex, cellCost, 'corner')) return null;

    const tile = buildTrack3dTiles(scratch)[pivotIndex];
    const target = cells[cells.length - 1];
    return {
      x: target.x,
      y: target.y,
      color: ARM_COLORS[armCells],
      cells,
      armCells,
      pivot: last,
      rotationSteps: tile?.rotationSteps ?? 0,
    };
  }

  /**
   * Toutes les cases cliquables en mode Tracer, avec leur couleur : la case
   * dorée du départ si la boucle peut se fermer, une case verte pour continuer
   * tout droit, et jusqu'à 3 cases (vert/jaune/rouge, Petit/Moyen/Large) par
   * direction perpendiculaire disponible — remplace l'ancien système
   * Départ/Ligne droite/Virage + boutons de taille : plus besoin de choisir un
   * outil avant de cliquer, la couleur de la case dit directement ce qu'elle fait.
   */
  function computeTraceCandidates(): TraceCandidate[] {
    const path = draft.path;
    if (path.length === 0) return [];
    const raw = getPlaceableDrawCells(draft);
    const start = path[0];
    const last = path[path.length - 1];
    const prev = path.length >= 2 ? path[path.length - 2] : null;
    const dir = prev ? { x: last.x - prev.x, y: last.y - prev.y } : null;

    const candidates: TraceCandidate[] = [];
    for (const c of raw) {
      if (c.x === start.x && c.y === start.y) {
        candidates.push({ x: c.x, y: c.y, color: HOVER_HIGHLIGHT_COLOR, cells: [c] });
        continue;
      }
      if (!dir) {
        // Pas encore de direction établie (juste après le départ) : simple
        // premier pas, aucun virage possible sans direction entrante.
        candidates.push({ x: c.x, y: c.y, color: ARM_COLORS[1], cells: [c] });
        continue;
      }
      const delta = { x: c.x - last.x, y: c.y - last.y };
      if (delta.x === dir.x && delta.y === dir.y) {
        candidates.push({ x: c.x, y: c.y, color: ARM_COLORS[1], cells: [c] });
        continue;
      }
      // Direction perpendiculaire : jusqu'à 3 tailles de virage possibles.
      for (const armCells of [1, 2, 3] as const) {
        const candidate = computeCornerCandidate(last, delta, armCells);
        if (candidate) candidates.push(candidate);
      }
    }
    return candidates;
  }

  interface DecorSnapResult {
    x: number;
    y: number;
    rotationDeg: number;
  }

  /**
   * Si « Coller » est actif et qu'un décor libre existe à portée
   * (`DECOR_SNAP_SEARCH_RADIUS`, hors `excludeId` — le décor qu'on déplace lui-même) :
   * accole `movingSlug` pile contre son bord, sans espace ni chevauchement, en
   * utilisant le gabarit réel des deux modèles (voir `decorModelInfoCache`), et lui
   * fait adopter la même rotation. Le bord retenu (avant/arrière/gauche/droite) est
   * celui du côté où se trouve `point` dans le repère local du voisin — le plus
   * proche des deux axes locaux l'emporte, comme un aimant qui accroche le côté le
   * plus proche plutôt qu'un coin. Sans voisin à portée, ou gabarit d'un des deux
   * modèles pas encore connu (voir `ensureDecorModelInfo`), retourne `point`/
   * `fallbackRotationDeg` inchangés (aucun collage, best-effort).
   */
  function computeDecorSnap(
    point: GroundPoint,
    movingSlug: string,
    fallbackRotationDeg: number,
    excludeId?: string
  ): DecorSnapResult {
    const fallback: DecorSnapResult = { x: point.x, y: point.y, rotationDeg: fallbackRotationDeg };
    if (!decorSnapEnabled) return fallback;
    const movingInfo = decorModelInfoCache.get(movingSlug);
    if (!movingInfo) return fallback;

    let neighbor: CircuitFreeDecor | null = null;
    let neighborDist = DECOR_SNAP_SEARCH_RADIUS;
    for (const decor of draft.freeDecor ?? []) {
      if (decor.id === excludeId) continue;
      const dist = Math.hypot(decor.x - point.x, decor.y - point.y);
      if (dist <= neighborDist) {
        neighbor = decor;
        neighborDist = dist;
      }
    }
    if (!neighbor) return fallback;
    const neighborInfo = decorModelInfoCache.get(neighbor.modelId);
    if (!neighborInfo) return fallback;

    // Repère local du voisin — même convention que le rendu réel
    // (`group.rotation.y = -(decor.rotationDeg * Math.PI) / 180` dans
    // `loadFreeTrackSceneDecor`) : `phi` transforme local → monde, son inverse
    // (transposée d'une rotation) transforme monde → local.
    const phi = -(neighbor.rotationDeg * Math.PI) / 180;
    const cos = Math.cos(phi);
    const sin = Math.sin(phi);
    const worldDx = point.x - neighbor.x;
    const worldDz = point.y - neighbor.y;
    const localX = worldDx * cos - worldDz * sin;
    const localZ = worldDx * sin + worldDz * cos;

    const onXAxis = Math.abs(localX) >= Math.abs(localZ);
    const sign = (onXAxis ? localX : localZ) >= 0 ? 1 : -1;
    const neighborHalf = (onXAxis ? neighborInfo.width : neighborInfo.depth) / 2;
    const movingHalf = (onXAxis ? movingInfo.width : movingInfo.depth) / 2;
    const localOffset = sign * (neighborHalf + movingHalf);
    const localOffsetX = onXAxis ? localOffset : 0;
    const localOffsetZ = onXAxis ? 0 : localOffset;

    // Retour local → monde (transform direct, même formule que `phi` ci-dessus).
    const worldOffsetX = localOffsetX * cos + localOffsetZ * sin;
    const worldOffsetZ = -localOffsetX * sin + localOffsetZ * cos;

    return {
      x: neighbor.x + worldOffsetX,
      y: neighbor.y + worldOffsetZ,
      rotationDeg: neighbor.rotationDeg,
    };
  }

  /** Aperçu fantôme (modèle .glb translucide, voir `setGhostModel`) de la pièce ou
   * du décor qui serait posé au point flottant `point` — reflète exactement la
   * position/rotation qu'utiliserait un clic à cet endroit :
   * - Décor déjà posé, sélectionné pour être déplacé : uniquement si « Coller »
   *   est actif (sinon l'ancien comportement — pas d'aperçu, juste le surlignage
   *   de case — reste inchangé) ; position/rotation collées au voisin le plus
   *   proche le cas échéant (voir `computeDecorSnap`), sauf en survolant le
   *   décor lui-même (qui se désélectionnerait plutôt que de se déplacer).
   * - Décor (placement libre) pas encore posé : position exacte + `decorRotationDeg`
   *   — ou collée au voisin le plus proche si « Coller » est actif — identique à
   *   `handleDecorClick`/`loadFreeTrackSceneDecor`.
   * - Pièce route : ancrée au centre de la case survolée, rotation dérivée du
   *   même `tile.rotationSteps` que le rendu réel (`tileRotationY` dans
   *   `circuit3dTrackScene.ts`) + le décalage `STRAIGHT_EXTRA_ROTATION` pour une
   *   ligne droite — PAS `tileStepToDirection`, qui ne sert qu'à stocker la
   *   rotation dans `Circuit.roadPieces`, pas à l'afficher. `null` si la case
   *   survolée ne peut pas recevoir la pièce sélectionnée (pas de tracé ici,
   *   forme route/virage incompatible) : pas d'aperçu plutôt qu'un aperçu faux.
   * Approximatif pour une pièce personnalisée multi-case dont le modèle partage
   * le pivot Kenney de la ligne droite (`sharesRoadPivot` dans
   * `circuit3dTrackScene.ts`) — l'aperçu utilise un centrage générique de son
   * propre modèle plutôt que ce pivot partagé, donc peut être légèrement décalé
   * pour ces pièces rares ; la pose réelle au clic, elle, reste exacte. */
  function computeGhostModelPreview(point: GroundPoint | null): GhostModelPreview | null {
    if (!point) return null;
    if (selectedFreeDecorId) {
      if (!decorSnapEnabled) return null;
      const selected = (draft.freeDecor ?? []).find((d) => d.id === selectedFreeDecorId);
      if (!selected) return null;
      if (Math.hypot(selected.x - point.x, selected.y - point.y) <= FREE_DECOR_HIT_RADIUS) {
        return null;
      }
      const info = decorModelInfoCache.get(selected.modelId);
      if (!info) return null;
      const snapped = computeDecorSnap(point, selected.modelId, selected.rotationDeg, selected.id);
      return {
        url: info.url,
        x: snapped.x,
        y: snapped.y,
        rotationDeg: snapped.rotationDeg,
        carModel: info.carModel,
      };
    }
    if (!selectedPiece) return null;
    if (selectedPiece.category === 'decor') {
      const snapped = computeDecorSnap(point, selectedPiece.slug, decorRotationDeg);
      return {
        url: selectedPiece.publicPath,
        x: snapped.x,
        y: snapped.y,
        rotationDeg: snapped.rotationDeg,
        carModel: selectedPiece.carModel,
      };
    }
    const cell = { x: Math.floor(point.x), y: Math.floor(point.y) };
    const pathIndex = findPathIndexAt(draft, cell.x, cell.y);
    if (pathIndex === null) return null;
    const tile = buildTrack3dTiles(draft)[pathIndex];
    const shape = selectedPiece.roadShape ?? 'straight';
    if (!tile || (tile.kind !== 'straight' && tile.kind !== 'corner')) return null;
    if ((tile.kind === 'corner') !== (shape === 'corner')) return null;
    const rotationDeg = shape === 'corner' ? tile.rotationSteps * 90 : tile.rotationSteps * 90 - 90;
    return {
      url: selectedPiece.publicPath,
      x: cell.x + 0.5,
      y: cell.y + 0.5,
      rotationDeg,
    };
  }

  /** Recalcule l'aperçu fantôme depuis le dernier point survolé — à appeler après
   * tout changement d'état qui affecte `computeGhostModelPreview` mais qui ne
   * vient pas lui-même d'un survol (sélection de pièce, bascule d'outil). */
  function refreshGhostModelPreview(): void {
    scene?.setGhostModel(isTrackReady() ? computeGhostModelPreview(lastHoverPoint) : null);
  }

  /** Surlignage rouge de ce qu'un clic droit retirerait sur `cell` — même ordre
   * de priorité/détection que `handleRightClick` en phase décoration, juste un
   * aperçu (case vide si rien à supprimer ici). Affiché par défaut au survol
   * dès qu'aucune pièce/décor n'est sélectionné pour être posé (le clic droit
   * étant désormais le seul geste de suppression, voir le bouton d'aide "?").
   * Le décor libre se cherche autour du point flottant réel (`lastHoverPoint`),
   * pas du centre de la case : un décor posé près d'un bord/coin peut dépasser
   * le rayon de détection depuis le centre alors qu'il reste bien sous le
   * curseur — décalage repéré en testant (le clic de suppression, lui,
   * utilisait déjà le point réel et fonctionnait). */
  function computeDeleteHoverHighlight(cell: GridCell): HighlightCell[] {
    const point = lastHoverPoint ?? { x: cell.x + 0.5, y: cell.y + 0.5 };
    const hasFreeDecor = !!findFreeDecorNear(draft, point.x, point.y, FREE_DECOR_HIT_RADIUS);
    const hasSomethingElse =
      !!findRoadPieceAt(draft, cell.x, cell.y) || !!findDecor3dAt(draft, cell.x, cell.y);
    if (!hasFreeDecor && !hasSomethingElse) return [];
    return [{ x: cell.x, y: cell.y, color: ERROR_HIGHLIGHT_COLOR }];
  }

  /** Cases candidates persistantes pour l'outil "Tour joker" une fois l'entrée
   * choisie — comme `updateTraceHighlight` pour le tracé principal, indépendant
   * du survol (les candidats sont peu nombreux, ~4 au plus, pas de bruit visuel
   * à les montrer tous en même temps) : violet pour continuer le branchement
   * (`getJokerLapPlaceableCells`), doré pour les cases du tracé principal qui le
   * refermeraient (adjacentes à la dernière case posée). Avant l'entrée, rien
   * ici — le survol case-par-case s'en charge (voir `onHoverCell`), tout le
   * tracé en violet serait trop chargé sur une grande grille. Vide une fois le
   * tour joker complet (plus rien à prévisualiser). */
  function refreshJokerLapHighlight(): void {
    if (!jokerLapToolActive) return;
    const jl = draft.jokerLap;
    if (!jl || jl.entryPathIndex < 0 || jl.exitPathIndex >= 0) {
      scene?.setHighlightCells([]);
      return;
    }
    const highlights: HighlightCell[] = getJokerLapPlaceableCells(draft).map((c) => ({
      x: c.x,
      y: c.y,
      color: JOKER_LAP_BRANCH_COLOR,
    }));
    if (jl.cells.length > 0) {
      const last = jl.cells[jl.cells.length - 1];
      const end = isPathClosed(draft.path) ? draft.path.length - 1 : draft.path.length;
      for (let i = 0; i < end; i++) {
        // La sortie doit différer de l'entrée (voir `setJokerLapExit`) — sans ce
        // garde-fou, l'entrée elle-même se surlignait comme candidate de sortie
        // valide alors que le clic dessus serait rejeté (repéré en testant).
        if (i === jl.entryPathIndex) continue;
        const pathCell = draft.path[i];
        if (areCellsAdjacent(last.x, last.y, pathCell.x, pathCell.y)) {
          highlights.push({ x: pathCell.x, y: pathCell.y, color: JOKER_LAP_EXIT_COLOR });
        }
      }
    }
    scene?.setHighlightCells(highlights);
  }

  /** Cases plaçables pendant le tracé (vert/jaune/rouge/doré selon l'action, voir
   * `computeTraceCandidates`) — vide une fois le tracé prêt (`isTrackReady()`),
   * où le survol pilote lui-même le surlignage (aperçu d'empreinte/suppression). */
  function updateTraceHighlight(): void {
    if (isTrackReady()) {
      scene?.setHighlightCells([]);
      return;
    }
    if (isDrawingStartLane()) {
      scene?.setHighlightCells(getStartLanePlaceableCells(draft));
      return;
    }
    scene?.setHighlightCells(computeTraceCandidates());
  }

  let scene: Circuit3dEditorSceneHandle | null = mountCircuit3dEditorScene(viewport, draft, {
    onHoverCell: (cell) => {
      hoveredCell = cell;
      renderHoverStatus();
      if (isTrackReady()) {
        if (jokerLapToolActive) {
          // Avant l'entrée : aperçu au survol case par case. Une fois l'entrée
          // posée, la liste de candidats est persistante (voir
          // `refreshJokerLapHighlight`) et ne dépend plus du survol.
          const jl = draft.jokerLap;
          if (!jl || jl.entryPathIndex < 0) {
            scene?.setHighlightCells(
              cell && findPathIndexAt(draft, cell.x, cell.y) !== null
                ? [{ x: cell.x, y: cell.y, color: JOKER_LAP_BRANCH_COLOR }]
                : []
            );
          }
          return;
        }
        if (selectedPiece || selectedFreeDecorId) {
          scene?.setHighlightCells(cell ? computeHoverFootprint(cell) : []);
          return;
        }
        // Rien à poser : aperçu de ce qu'un clic droit retirerait ici.
        scene?.setHighlightCells(cell ? computeDeleteHoverHighlight(cell) : []);
        return;
      }
      // Aperçu fantôme 3D du virage survolé (voir `setGhostCorner`) : uniquement
      // pendant le tracé actif, sur une case qui correspond à un virage cliquable.
      if (!isDrawingStartLane() && !isPathClosed(draft.path)) {
        const candidate = cell
          ? computeTraceCandidates().find((c) => c.x === cell.x && c.y === cell.y)
          : undefined;
        scene?.setGhostCorner(
          candidate?.armCells !== undefined &&
            candidate.pivot !== undefined &&
            candidate.rotationSteps !== undefined
            ? {
                pivot: candidate.pivot,
                rotationSteps: candidate.rotationSteps,
                armCells: candidate.armCells,
                color: candidate.color,
              }
            : null
        );
      } else {
        scene?.setGhostCorner(null);
      }
    },
    onHoverPoint: (point) => {
      lastHoverPoint = point;
      if (isTrackReady()) {
        scene?.setGhostModel(computeGhostModelPreview(point));
      }
    },
    onCellClick: (cell, point) => {
      if (!isTrackReady()) {
        if (isDrawingStartLane()) handleStartLaneClick(cell);
        else handleTraceClick(cell);
      } else {
        handleEditClick(cell, point);
      }
    },
    onCellRightClick: (cell, point) => handleRightClick(cell, point),
    onMiddleDragRotate: (deltaDeg) => {
      if (selectedFreeDecorId) {
        const current = (draft.freeDecor ?? []).find((d) => d.id === selectedFreeDecorId);
        if (!current || !scene) return;
        draft = rotateFreeDecor(draft, selectedFreeDecorId, current.rotationDeg + deltaDeg);
        scene.refreshScene(draft);
        return;
      }
      if (selectedPiece?.category === 'decor') {
        decorRotationDeg = (decorRotationDeg + deltaDeg + 360) % 360;
        refreshGhostModelPreview();
      }
    },
    onShiftDragElevate: (deltaElevation) => {
      if (!selectedFreeDecorId) return;
      const current = (draft.freeDecor ?? []).find((d) => d.id === selectedFreeDecorId);
      if (!current || !scene) return;
      draft = setFreeDecorElevation(
        draft,
        selectedFreeDecorId,
        (current.elevation ?? 0) + deltaElevation
      );
      scene.refreshScene(draft);
    },
  });
  refreshValidation();
  updateTraceHighlight();

  /** Mode Tracer : construit le tracé case par case (logique partagée de l'éditeur
   * 2D) — voir le docstring de `mountCircuit3dEditor`. Chaque clic pose la case
   * cliquée en fonction de sa couleur (voir `computeTraceCandidates`) : simple
   * case droite/fermeture pour la case dorée du départ, ou virage complet
   * (case de virage + tout son bras, en un seul clic) pour vert/jaune/rouge.
   */
  function handleTraceClick(cell: GridCell): void {
    if (!scene) return;

    if (draft.path.length === 0) {
      const result = tryDrawPathCell(draft, cell.x, cell.y);
      if ('error' in result) {
        actionStatus = result.error;
        actionIsError = true;
        renderActionStatus();
        return;
      }
      draft = result.circuit;
      // Ligne de départ tracée EN PREMIER, juste après la case de départ — pas
      // après la fermeture de la boucle (voir `isDrawingStartLane`) : le point
      // de jonction est toujours la case de départ elle-même (index 0).
      draft = setStartLaneJoin(draft, 0);
      actionStatus =
        'Départ placé — tracez maintenant la ligne de départ (case verte à côté du départ), ou « Passer » pour vous en passer.';
      actionIsError = false;
      scene.refreshScene(draft);
      updateTraceHighlight();
      scene.setGhostCorner(null);
      renderActionStatus();
      refreshValidation();
      updateInstructions();
      renderTraceTools();
      return;
    }

    const candidate = computeTraceCandidates().find((c) => c.x === cell.x && c.y === cell.y);
    if (!candidate) {
      actionStatus =
        'Cette case n’est pas un choix valide — cliquez une case colorée (vert/jaune/rouge), ou la case dorée du départ pour fermer la boucle.';
      actionIsError = true;
      renderActionStatus();
      return;
    }

    // Pour un virage, le pivot est `cell` juste avant ce clic (déjà dans le
    // tracé) — voir `computeCornerCandidate`. Pour tout droit/fermeture, cette
    // valeur n'est pas utilisée (`candidate.armCells` reste `undefined`).
    const pivotIndex = draft.path.length - 1;
    let closedNow = false;
    for (const step of candidate.cells) {
      const result = tryDrawPathCell(draft, step.x, step.y);
      if ('error' in result) {
        // Ne devrait pas arriver (déjà validé par simulation au moment du calcul
        // du candidat) — filet de sécurité si le tracé a changé sous nos pieds.
        actionStatus = result.error;
        actionIsError = true;
        renderActionStatus();
        return;
      }
      draft = result.circuit;
      if (result.closed) closedNow = true;
    }

    if (candidate.armCells) {
      const tile = buildTrack3dTiles(draft)[pivotIndex];
      if (tile && tile.kind === 'corner') {
        const rotation = tileStepToDirection(tile.rotationSteps);
        const cellCost = (candidate.armCells * 2 + 1) as 3 | 5 | 7;
        const result = addRoadPiece(
          draft,
          'path',
          pivotIndex,
          cellCost,
          proceduralCornerSlug(candidate.armCells),
          rotation,
          'corner'
        );
        if ('circuit' in result) draft = result.circuit;
      }
    }

    // La ligne de départ est déjà tracée à ce stade (voir la case départ plus
    // haut) — fermer la boucle est maintenant la toute dernière étape.
    actionStatus = closedNow
      ? 'Circuit fermé ✓'
      : candidate.armCells
        ? `Virage « ${ARM_LABELS[candidate.armCells]} » posé.`
        : 'Case ajoutée au tracé.';
    actionIsError = false;
    scene.refreshScene(draft);
    updateTraceHighlight();
    scene.setGhostCorner(null); // le survol courant ne correspond plus forcément à rien de valide
    renderActionStatus();
    refreshValidation();
    updateInstructions();
    renderTraceTools();
  }

  /** Trace la ligne de départ (5 cases exactement, alignées, depuis le point de
   * jonction) une fois la boucle fermée — voir `isDrawingStartLane`. */
  function handleStartLaneClick(cell: GridCell): void {
    if (!scene) return;
    const result = tryAppendStartLaneCell(draft, cell.x, cell.y);
    if ('error' in result) {
      actionStatus = result.error;
      actionIsError = true;
      renderActionStatus();
      return;
    }
    draft = result.circuit;
    const remaining = START_LANE_LENGTH - (draft.startLane?.cells.length ?? 0);
    actionStatus =
      remaining > 0 ? 'Case de ligne de départ ajoutée.' : 'Ligne de départ complète (5 cases).';
    actionIsError = false;
    scene.refreshScene(draft);
    updateTraceHighlight();
    renderActionStatus();
    refreshValidation();
    updateInstructions();
    renderTraceTools();
  }

  /** Outil "Tour joker" (voir `jokerLapToolActive`) : entrée sur le tracé
   * principal, puis cases hors tracé pour le branchement, puis sortie sur le
   * tracé principal adjacente à la dernière case posée — mêmes trois étapes
   * que l'éditeur 2D (`circuitEditor.ts`), via la même API partagée
   * (`shared/game/circuitJokerLapEdit.ts`), aucune règle dupliquée. Le rendu
   * 3D du branchement complet est déjà géré par `buildJokerLap3dTiles`
   * (appelé inconditionnellement par `circuit3dEditorScene.ts#buildAllTiles`)
   * — `scene.refreshScene(draft)` suffit dès que la sortie est posée. */
  function handleJokerLapToolClick(cell: GridCell): void {
    if (!scene) return;
    const jl = draft.jokerLap;

    if (!jl || jl.entryPathIndex < 0) {
      const pathIndex = findPathIndexAt(draft, cell.x, cell.y);
      if (pathIndex === null) {
        actionStatus = 'Cliquez une case du circuit principal pour définir l’entrée.';
        actionIsError = true;
        renderActionStatus();
        return;
      }
      draft = setJokerLapEntry(draft, pathIndex);
      actionStatus = 'Entrée définie — tracez le passage alternatif (cases hors circuit).';
      actionIsError = false;
    } else if (jl.exitPathIndex < 0) {
      const pathIndex = findPathIndexAt(draft, cell.x, cell.y);
      if (pathIndex !== null && jl.cells.length > 0) {
        const result = setJokerLapExit(draft, pathIndex);
        if ('error' in result) {
          actionStatus = result.error;
          actionIsError = true;
          renderActionStatus();
          return;
        }
        draft = result.circuit;
        actionStatus = 'Tour joker terminé.';
        actionIsError = false;
      } else {
        const result = tryAppendJokerLapCell(draft, cell.x, cell.y);
        if ('error' in result) {
          actionStatus = result.error;
          actionIsError = true;
          renderActionStatus();
          return;
        }
        draft = result.circuit;
        actionStatus = 'Case ajoutée au tour joker.';
        actionIsError = false;
      }
    } else {
      actionStatus = 'Tour joker déjà complet — « Supprimer le tour joker » pour recommencer.';
      actionIsError = true;
      renderActionStatus();
      return;
    }

    scene.refreshScene(draft);
    refreshJokerLapHighlight();
    renderActionStatus();
    refreshValidation();
    updateInstructions();
    renderToolbar();
  }

  /** Outil "Surface" (voir `surfaceBrush`) : clic sur une case du tracé principal
   * pour la peindre en terre/bitume, ou l'effacer d'un second clic — même
   * restriction que la pose de pièce ci-dessous (lignes droites et virages
   * seulement, le tour joker/la ligne de départ ne sont pas encore gérés en 3D). */
  function handleSurfaceBrushClick(cell: GridCell): void {
    if (!scene || !surfaceBrush) return;
    const pathIndex = findPathIndexAt(draft, cell.x, cell.y);
    const tile = pathIndex !== null ? buildTrack3dTiles(draft)[pathIndex] : undefined;
    if (!tile || (tile.kind !== 'straight' && tile.kind !== 'corner')) {
      actionStatus =
        'Seules les lignes droites et les virages du tracé principal peuvent être peints.';
      actionIsError = true;
      renderActionStatus();
      return;
    }
    const current = getCircuitCellStyle(draft, cell.x, cell.y)?.fill ?? null;
    const next = current === surfaceBrush ? null : surfaceBrush;
    draft = setCircuitCellFill(draft, cell.x, cell.y, next);
    actionStatus = next
      ? `Case (${cell.x}, ${cell.y}) peinte en ${next === 'earth' ? 'terre' : 'bitume'}.`
      : `Case (${cell.x}, ${cell.y}) repeinte par défaut.`;
    actionIsError = false;
    scene.refreshScene(draft);
    scene.setHighlightCells([]);
    renderActionStatus();
    refreshValidation();
    updateInstructions();
  }

  function handleEditClick(cell: GridCell, point: GroundPoint): void {
    if (!scene) return;

    if (jokerLapToolActive) {
      handleJokerLapToolClick(cell);
      return;
    }

    if (surfaceBrush) {
      handleSurfaceBrushClick(cell);
      return;
    }

    // Décor libre déjà sélectionné : ce clic le déplace ici — sauf s'il retombe
    // sur le décor lui-même, auquel cas il se désélectionne sans se déplacer.
    if (selectedFreeDecorId) {
      const selected = (draft.freeDecor ?? []).find((d) => d.id === selectedFreeDecorId);
      const clickedSame =
        !!selected &&
        Math.hypot(selected.x - point.x, selected.y - point.y) <= FREE_DECOR_HIT_RADIUS;
      selectedFreeDecorId = null;
      if (selected && !clickedSame) {
        const snapped = computeDecorSnap(
          point,
          selected.modelId,
          selected.rotationDeg,
          selected.id
        );
        draft = moveFreeDecor(draft, selected.id, snapped.x, snapped.y);
        draft = rotateFreeDecor(draft, selected.id, snapped.rotationDeg);
        actionStatus = 'Décor déplacé.';
      } else {
        actionStatus = 'Décor désélectionné.';
      }
      actionIsError = false;
      scene.refreshScene(draft);
      scene.setHighlightCells([]);
      refreshGhostModelPreview();
      renderActionStatus();
      refreshValidation();
      updateInstructions();
      renderToolbar();
      return;
    }

    // Sélection d'un décor libre existant — vérifiée avant le retrait des pièces
    // classiques ci-dessous : un décor libre peut se superposer à une pièce route
    // ou un décor à l'ancienne case entière, il doit rester atteignable au clic.
    const existingFreeDecor = findFreeDecorNear(draft, point.x, point.y, FREE_DECOR_HIT_RADIUS);
    if (existingFreeDecor) {
      selectedFreeDecorId = existingFreeDecor.id;
      ensureDecorModelInfo(existingFreeDecor.modelId);
      actionStatus =
        'Décor sélectionné — cliquez ailleurs pour le déplacer, clic molette + glisser pour le faire pivoter, Maj + clic gauche + glisser pour le monter/descendre, ou « Supprimer » pour le retirer.';
      actionIsError = false;
      scene.setHighlightCells([
        {
          x: Math.round(existingFreeDecor.x),
          y: Math.round(existingFreeDecor.y),
          color: HOVER_HIGHLIGHT_COLOR,
        },
      ]);
      refreshGhostModelPreview();
      renderActionStatus();
      updateInstructions();
      renderToolbar();
      return;
    }

    if (!selectedPiece) {
      actionStatus = 'Choisissez une pièce dans le catalogue avant de cliquer sur une case.';
      actionIsError = true;
      renderActionStatus();
      return;
    }

    if (selectedPiece.category === 'decor') {
      handleDecorClick(point, selectedPiece);
      return;
    }

    const pathIndex = findPathIndexAt(draft, cell.x, cell.y);
    if (pathIndex === null) {
      actionStatus = 'Cette case ne fait pas partie du tracé principal (pas encore géré en 3D).';
      actionIsError = true;
      renderActionStatus();
      return;
    }

    const tile = buildTrack3dTiles(draft)[pathIndex];
    const cellCost = pieceCellCost(selectedPiece);
    const shape = selectedPiece.roadShape ?? 'straight';
    if (!tile || (tile.kind !== 'straight' && tile.kind !== 'corner')) {
      actionStatus =
        'Seules les lignes droites et les virages peuvent recevoir une pièce pour l’instant.';
      actionIsError = true;
      renderActionStatus();
      return;
    }
    if (tile.kind === 'corner' && shape !== 'corner') {
      actionStatus =
        'Cette pièce est une ligne droite — choisissez plutôt un modèle de virage ici.';
      actionIsError = true;
      renderActionStatus();
      return;
    }
    if (tile.kind === 'straight' && shape !== 'straight') {
      actionStatus = 'Cette pièce est un virage — choisissez plutôt un modèle de ligne droite ici.';
      actionIsError = true;
      renderActionStatus();
      return;
    }

    // Rotation toujours dérivée du tracé existant, jamais un choix libre : sur une
    // ligne droite, `facingSteps` (= `rotationSteps`) donne le sens de la run ; sur
    // un virage, `rotationSteps` donne l'orientation qui touche les bons côtés de case
    // — y compris pour un virage Large/Larger multi-case, dont la rotation reste
    // celle de la case de virage centrale (les bras adjacents n'en ont pas besoin,
    // ils sont validés par leur seule rectitude côté partagé).
    // `tileStepToDirection` convertit la convention locale à track3dLayout.ts vers
    // l'enum partagé `Direction` (les deux encodages sont décalés d'un cran).
    const rotation = tileStepToDirection(
      tile.kind === 'corner' ? tile.rotationSteps : tile.facingSteps
    );

    const result = addRoadPiece(
      draft,
      'path',
      pathIndex,
      cellCost,
      selectedPiece.slug,
      rotation,
      shape
    );
    if ('error' in result) {
      actionStatus = result.error;
      actionIsError = true;
      renderActionStatus();
      return;
    }
    draft = result.circuit;
    actionStatus = `${selectedPiece.displayName} posée sur (${cell.x}, ${cell.y}).`;
    actionIsError = false;
    scene.refreshScene(draft);
    scene.setHighlightCells([]);
    renderActionStatus();
    refreshValidation();
    updateInstructions();
  }

  /** Pose un décor en placement libre, au point flottant exact sous le curseur —
   * ou collé contre son voisin le plus proche si « Coller » est actif (voir
   * `computeDecorSnap`) — sans empreinte figée façon gradins 2D, sans
   * vérification d'occupation (voir `shared/game/circuitFreeDecorEdit.ts`). */
  function handleDecorClick(point: GroundPoint, piece: CircuitPiece3dCatalogEntry): void {
    if (!scene) return;
    const snapped = computeDecorSnap(point, piece.slug, decorRotationDeg);
    draft = addFreeDecor(draft, {
      kind: piece.decorKind ?? 'prop',
      modelId: piece.slug,
      x: snapped.x,
      y: snapped.y,
      rotationDeg: snapped.rotationDeg,
    });
    actionStatus = `${piece.displayName} posé.`;
    actionIsError = false;
    scene.refreshScene(draft);
    scene.setHighlightCells([]);
    renderActionStatus();
    refreshValidation();
    updateInstructions();
  }

  /** Ouvre le sélecteur combiné (onglets Pièces/Décor, voir
   * `circuitPiecePicker.ts`) — un seul bouton d'entrée pour les deux
   * catégories, le clic sur un item lit `item.category` pour savoir comment
   * le poser ensuite (route ancrée sur le tracé, ou décor en placement libre). */
  function openPicker(triggerButton: HTMLElement): void {
    // Libère le contexte WebGL de l'éditeur pendant que la modale affiche ses
    // propres aperçus 3D : sinon le total de contextes actifs peut dépasser la
    // limite du navigateur et faire perdre celui de l'éditeur (canvas blanc).
    scene?.pauseForOverlay();
    openCircuitPiecePicker({
      title: 'Décor / Pièce',
      currentSlug: selectedPiece?.slug ?? null,
      onClose: () => scene?.resumeFromOverlay(),
      onSelect: (item) => {
        selectedPiece = item;
        jokerLapToolActive = false;
        surfaceBrush = null;
        if (item.category === 'decor') ensureDecorModelInfo(item.slug);
        const costLabel = pieceCellCost(item) > 1 ? ` (${pieceCellCost(item)} cases)` : '';
        setPillButtonLabel(
          triggerButton,
          `${item.category === 'decor' ? 'Décor' : 'Pièce'} : ${item.displayName}${costLabel}`
        );
        actionStatus = '';
        actionIsError = false;
        renderActionStatus();
        updateInstructions();
        renderToolbar();
        if (hoveredCell) scene?.setHighlightCells(computeHoverFootprint(hoveredCell));
        refreshGhostModelPreview();
      },
    });
  }

  /**
   * Clic droit unifié — remplace l'ancien bouton "Effacer dernière case" ET
   * l'ancien outil "Supprimer" à activer/désactiver (voir le popover d'aide
   * "?" pour l'explication faite à l'utilisateur) :
   * - Tant que le tracé n'est pas prêt (`!isTrackReady()`) : annule la
   *   DERNIÈRE case posée, quelle que soit la position cliquée — priorité au
   *   branchement du tour joker en cours, puis à la ligne de départ, puis au
   *   tracé principal (rouvre la boucle si besoin). Reprend telle quelle la
   *   logique de l'ancien bouton "Effacer dernière case".
   * - Une fois le tracé prêt : retire la pièce de route/le décor SOUS LE
   *   CURSEUR (décor libre proche du point → pièce de route sur la case →
   *   décor à l'ancienne case). Reprend telle quelle la logique de l'ancien
   *   `handleDeleteToolClick`.
   */
  function handleRightClick(cell: GridCell, point: GroundPoint): void {
    if (!scene) return;

    if (jokerLapToolActive) {
      const jl = draft.jokerLap;
      if (jl && jl.cells.length > 0) {
        draft = undoLastJokerLapCell(draft) ?? draft;
        actionStatus = 'Dernière case du tour joker retirée.';
        actionIsError = false;
      } else if (jl && jl.entryPathIndex >= 0) {
        draft = clearJokerLap(draft);
        jokerLapToolActive = false;
        actionStatus = 'Entrée du tour joker annulée.';
        actionIsError = false;
      } else {
        actionStatus = 'Rien à annuler.';
        actionIsError = true;
        renderActionStatus();
        return;
      }
      scene.refreshScene(draft);
      refreshJokerLapHighlight();
      renderActionStatus();
      refreshValidation();
      updateInstructions();
      renderToolbar();
      return;
    }

    if (isDrawingStartLane()) {
      // La ligne de départ se trace EN PREMIER (voir `isDrawingStartLane`) :
      // tant qu'elle est incomplète, l'annulation la défait case par case,
      // puis, une fois vide, retire la case de départ elle-même (rien
      // d'autre à défaire avant elle — la boucle principale n'existe pas
      // encore).
      if (draft.startLane!.cells.length > 0) {
        draft = undoLastStartLaneCell(draft) ?? draft;
        actionStatus = 'Dernière case de ligne de départ retirée.';
      } else {
        draft = clearStartLane(draft);
        draft = undoLastPathCell(draft) ?? draft;
        actionStatus = 'Départ retiré.';
      }
      actionIsError = false;
      scene.refreshScene(draft);
      updateTraceHighlight();
      scene.setGhostCorner(null);
      renderActionStatus();
      refreshValidation();
      updateInstructions();
      renderTraceTools();
      renderToolbar();
      return;
    }

    if (!isPathClosed(draft.path)) {
      // Ligne de départ déjà complète : une fois le tracé principal défait
      // jusqu'à la case de départ seule, l'annulation continue dans la ligne
      // de départ (sinon plus aucun moyen de la retoucher une fois le tracé
      // principal repris — elle resterait hors de portée de l'annulation).
      if (draft.path.length <= 1 && draft.startLane) {
        draft = undoLastStartLaneCell(draft) ?? draft;
        actionStatus = 'Dernière case de ligne de départ retirée.';
        actionIsError = false;
        scene.refreshScene(draft);
        updateTraceHighlight();
        scene.setGhostCorner(null);
        renderActionStatus();
        refreshValidation();
        updateInstructions();
        renderTraceTools();
        renderToolbar();
        return;
      }
      const next = undoLastPathCell(draft);
      if (!next) {
        actionStatus = 'Rien à effacer.';
        actionIsError = true;
        renderActionStatus();
        return;
      }
      // `undoLastPathCell` ne purge pas `roadPieces` lui-même (voir
      // `pruneRoadPieces`) : sans ça, une pièce déjà matérialisée avant la case
      // retirée reste dans le brouillon en référençant un tracé raccourci — bug
      // réel, repéré en testant l'undo après un virage groupé (voir le garde-fou
      // équivalent ajouté dans `track3dLayout.ts#applyMainPathRoadPieceSlugs`).
      draft = pruneRoadPieces(next);
      actionStatus = 'Dernière case retirée.';
      actionIsError = false;
      scene.refreshScene(draft);
      updateTraceHighlight();
      scene.setGhostCorner(null);
      renderActionStatus();
      refreshValidation();
      updateInstructions();
      renderTraceTools();
      renderToolbar();
      return;
    }

    // Tracé prêt : suppression position-based (décor libre proche du point →
    // pièce de route sur la case → décor à l'ancienne case).
    const existingFreeDecor = findFreeDecorNear(draft, point.x, point.y, FREE_DECOR_HIT_RADIUS);
    if (existingFreeDecor) {
      draft = removeFreeDecor(draft, existingFreeDecor.id);
      actionStatus = 'Décor retiré.';
      actionIsError = false;
      scene.refreshScene(draft);
      scene.setHighlightCells([]);
      renderActionStatus();
      refreshValidation();
      updateInstructions();
      return;
    }
    const existingRoadPiece = findRoadPieceAt(draft, cell.x, cell.y);
    if (existingRoadPiece) {
      draft = removeRoadPiece(draft, existingRoadPiece.id);
      actionStatus = 'Pièce retirée — case(s) remise(s) au modèle par défaut.';
      actionIsError = false;
      scene.refreshScene(draft);
      scene.setHighlightCells([]);
      renderActionStatus();
      refreshValidation();
      updateInstructions();
      return;
    }
    const existingDecor = findDecor3dAt(draft, cell.x, cell.y);
    if (existingDecor) {
      draft = removeDecor3d(draft, existingDecor.id);
      actionStatus = 'Décor retiré.';
      actionIsError = false;
      scene.refreshScene(draft);
      scene.setHighlightCells([]);
      renderActionStatus();
      refreshValidation();
      updateInstructions();
      return;
    }
    actionStatus = 'Rien à supprimer ici.';
    actionIsError = true;
    renderActionStatus();
  }

  const reopenTraceButton = createPillButton('↶ Reprendre le tracé', {
    variant: 'soft',
    small: true,
    onClick: () => {
      const next = undoLastPathCell(draft);
      if (!next) return;
      draft = pruneRoadPieces(next);
      actionStatus =
        'Tracé rouvert — dernière case retirée, vous pouvez continuer ou refermer la boucle différemment.';
      actionIsError = false;
      scene?.refreshScene(draft);
      updateTraceHighlight();
      scene?.setGhostCorner(null);
      refreshGhostModelPreview();
      renderActionStatus();
      refreshValidation();
      updateInstructions();
      renderTraceTools();
      renderToolbar();
    },
  });
  const pieceOrDecorButton = createPillButton('🧩 Décor / Pièce', {
    variant: 'soft',
    small: true,
    onClick: () => openPicker(pieceOrDecorButton),
  });
  /** "🔀 Tour joker" tant qu'aucune entrée n'est posée (le clic démarre/annule
   * l'intention), "🔀 Effacer tour joker" dès qu'une entrée existe — qu'il
   * soit en cours de construction ou complet (le clic le supprime alors
   * entièrement). Plus de bouton séparé pour annuler une case du branchement
   * en cours : c'est le clic droit unifié qui s'en charge (`handleRightClick`). */
  function jokerLapButtonLabel(): string {
    return draft.jokerLap && draft.jokerLap.entryPathIndex >= 0
      ? '🔀 Effacer tour joker'
      : '🔀 Tour joker';
  }
  const jokerLapToolButton = createPillButton(jokerLapButtonLabel(), {
    variant: 'soft',
    small: true,
    onClick: () => {
      const jl = draft.jokerLap;
      if (jl && jl.entryPathIndex >= 0) {
        draft = clearJokerLap(draft);
        jokerLapToolActive = false;
        actionStatus = 'Tour joker supprimé.';
        actionIsError = false;
        scene?.refreshScene(draft);
        scene?.setHighlightCells([]);
        renderActionStatus();
        refreshValidation();
        updateInstructions();
        renderToolbar();
        return;
      }
      jokerLapToolActive = !jokerLapToolActive;
      if (jokerLapToolActive) {
        selectedPiece = null;
        selectedFreeDecorId = null;
        surfaceBrush = null;
      }
      scene?.setHighlightCells([]);
      refreshGhostModelPreview();
      refreshJokerLapHighlight();
      actionStatus = jokerLapToolActive
        ? 'Outil tour joker actif — cliquez une case du circuit principal pour l’entrée.'
        : '';
      actionIsError = false;
      renderActionStatus();
      updateInstructions();
      renderToolbar();
    },
  });
  /** Bouton pill unique (voir `surfaceBrush`) qui fait défiler les trois états
   * inactif → terre → bitume → inactif à chaque clic, plutôt que deux boutons
   * séparés — un seul outil de peinture, un seul contrôle. */
  function surfaceBrushLabel(): string {
    if (surfaceBrush === 'earth') return '🟫 Terre';
    if (surfaceBrush === 'asphalt') return '⬛ Bitume';
    return '🎨 Surface';
  }
  function cycleSurfaceBrush(): void {
    surfaceBrush = surfaceBrush === null ? 'earth' : surfaceBrush === 'earth' ? 'asphalt' : null;
    if (surfaceBrush) {
      selectedPiece = null;
      selectedFreeDecorId = null;
      jokerLapToolActive = false;
    }
    scene?.setHighlightCells([]);
    refreshGhostModelPreview();
    actionStatus = surfaceBrush
      ? `Outil surface actif — cliquez une ligne droite ou un virage pour le peindre en ${surfaceBrush === 'earth' ? 'terre' : 'bitume'}.`
      : '';
    actionIsError = false;
    renderActionStatus();
    updateInstructions();
    renderToolbar();
  }
  const surfaceBrushButton = createPillButton(surfaceBrushLabel(), {
    variant: 'soft',
    small: true,
    onClick: cycleSurfaceBrush,
  });
  const topViewButton = createPillButton('Vue du dessus', {
    variant: 'soft',
    small: true,
    onClick: () => {
      if (!scene) return;
      const next = !scene.isTopView();
      scene.setTopView(next);
      setPillButtonLabel(topViewButton, next ? 'Vue 3D' : 'Vue du dessus');
    },
  });
  const recenterButton = createPillButton('⌖ Recentrer', {
    variant: 'soft',
    small: true,
    onClick: () => scene?.recenter(),
  });
  /** Points de repère aux coins de chaque case (rouge = style par défaut, violet
   * = case couverte par une pièce groupée), calculés uniquement depuis la grille
   * — permet de vérifier à l'œil si un virage Petit/Moyen/Large groupé se raccorde
   * vraiment à ses voisins, sans deviner sur le rendu 3D seul. */
  let debugMarkersOn = false;
  const debugMarkersButton = createPillButton('📍 Repères', {
    variant: 'soft',
    small: true,
    onClick: () => {
      debugMarkersOn = !debugMarkersOn;
      scene?.setDebugCornerMarkers(debugMarkersOn);
      setPillButtonLabel(debugMarkersButton, debugMarkersOn ? '📍 Repères ✓' : '📍 Repères');
    },
  });
  /** Bascule « Coller » (voir `computeDecorSnap`) — n'a d'effet que sur la pose/le
   * déplacement d'un décor libre, mais reste toujours visible dans la barre comme
   * les autres bascules ci-dessus, sans condition de mode. */
  const decorSnapButton = createPillButton('🧲 Coller', {
    variant: 'soft',
    small: true,
    onClick: () => {
      decorSnapEnabled = !decorSnapEnabled;
      setPillButtonLabel(decorSnapButton, decorSnapEnabled ? '🧲 Coller ✓' : '🧲 Coller');
      refreshGhostModelPreview();
    },
  });

  /** Petit bouton rond icône seule (bascule position, aide, enregistrer) —
   * distinct des pilules texte+icône ci-dessus (voir `.circuit-3d-editor-bar-icon-btn`). */
  function createIconButton(
    icon: string,
    ariaLabel: string,
    onClick: (e: MouseEvent) => void
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'circuit-3d-editor-bar-icon-btn';
    btn.setAttribute('aria-label', ariaLabel);
    btn.title = ariaLabel;
    btn.textContent = icon;
    btn.addEventListener('click', onClick);
    return btn;
  }

  const positionToggleButton = createIconButton('⇅', 'Basculer la barre en haut/en bas', () => {
    barOnTop = !barOnTop;
    sessionStorage.setItem(BAR_POSITION_KEY, barOnTop ? 'top' : 'bottom');
    applyBarPosition();
  });

  const helpPopover = document.createElement('div');
  helpPopover.className = 'circuit-3d-editor-help-popover';
  helpPopover.hidden = true;
  helpPopover.innerHTML = `
    <p><strong>Clic gauche</strong> : pose la pièce/le décor sélectionné, ou trace le circuit.</p>
    <p><strong>Clic droit</strong> : pendant le tracé, annule la dernière case posée (n'importe où). Une fois le circuit terminé, retire la pièce ou le décor sous le curseur.</p>
    <p><strong>Clic molette + glisser</strong> : fait pivoter le décor sélectionné, ou celui qui va être posé.</p>
    <p><strong>🧲 Coller</strong> : pose/déplace un décor libre accolé pile contre le bord du décor le plus proche (même rotation), au lieu du point exact sous le curseur.</p>
    <p>« Décor / Pièce » et « Tour joker » se débloquent une fois le tracé et la ligne de départ terminés.</p>
  `;
  // Le clic (en plus du survol ci-dessous) bascule l'affichage — nécessaire
  // sur mobile/tactile, où il n'existe pas de survol, et où ce bouton vit
  // maintenant dans le tiroir d'outils (voir plus bas) : sans
  // `stopPropagation`, ce clic remonterait jusqu'au tiroir et le refermerait
  // avant que l'aide n'ait eu le temps de s'afficher.
  const helpButton = createIconButton('?', 'Aide', (e) => {
    e.stopPropagation();
    helpPopover.hidden = !helpPopover.hidden;
  });
  const helpWrap = document.createElement('div');
  helpWrap.className = 'circuit-3d-editor-help-wrap';
  helpWrap.append(helpButton, helpPopover);
  // Affichage au survol (mouseenter/mouseleave) en plus du clic ci-dessus :
  // `helpWrap` englobe bouton ET popover, donc passer de l'un à l'autre à la
  // souris ne déclenche jamais `mouseleave` (relation parent/enfant DOM,
  // indépendante de la position visuelle du popover).
  helpWrap.addEventListener('mouseenter', () => {
    helpPopover.hidden = false;
  });
  helpWrap.addEventListener('mouseleave', () => {
    helpPopover.hidden = true;
  });

  const saveButton = createIconButton('✓', 'Enregistrer', () => void persist());
  saveButton.classList.add('circuit-3d-editor-bar-icon-btn--save');
  function setSaveButtonBusy(busy: boolean): void {
    saveButton.disabled = busy;
    saveButton.innerHTML = busy
      ? '<span class="spinner circuit-3d-editor-bar-spinner"></span>'
      : '✓';
  }

  /** Sur mobile, la barre n'affiche plus que « Outils » + Enregistrer — jusqu'à
   * 8 pilules + 3 icônes d'un coup sur un téléphone rendaient la barre
   * illisible (repéré sur deux captures d'écran) ; deux tentatives
   * intermédiaires (tout garder en ligne, puis un sous-menu « ⋯ Plus » en
   * popover) n'ont pas suffi. Les autres outils vivent dans un tiroir
   * plein écran ouvert par « Outils », qui se referme après avoir choisi un
   * outil pour retomber directement sur la vue 3D — le geste demandé.
   * Seuil aligné sur le point de rupture CSS `@media (max-width: 599px)`. */
  const MOBILE_TOOLBAR_BREAKPOINT = 599;
  function isMobileToolbar(): boolean {
    return window.innerWidth <= MOBILE_TOOLBAR_BREAKPOINT;
  }

  const drawerTitle = document.createElement('span');
  drawerTitle.className = 'circuit-3d-editor-drawer-title';
  drawerTitle.textContent = 'Outils';
  const drawerCloseButton = createIconButton('✕', 'Fermer', () => closeToolsDrawer());
  const drawerHeader = document.createElement('div');
  drawerHeader.className = 'circuit-3d-editor-drawer-header';
  drawerHeader.append(drawerTitle, drawerCloseButton);

  const drawerPanel = document.createElement('div');
  drawerPanel.className = 'circuit-3d-editor-drawer-panel';
  const drawerBackdrop = document.createElement('div');
  drawerBackdrop.className = 'circuit-3d-editor-drawer-backdrop';
  root.append(drawerBackdrop, drawerPanel);

  function closeToolsDrawer(): void {
    drawerPanel.classList.remove('circuit-3d-editor-drawer-panel--open');
    drawerBackdrop.classList.remove('circuit-3d-editor-drawer-backdrop--open');
  }
  function openToolsDrawer(): void {
    drawerPanel.classList.add('circuit-3d-editor-drawer-panel--open');
    drawerBackdrop.classList.add('circuit-3d-editor-drawer-backdrop--open');
  }
  drawerBackdrop.addEventListener('click', closeToolsDrawer);
  // Referme après avoir choisi un outil (mais pas au clic sur l'entête/le
  // bouton fermer lui-même, déjà géré par `closeToolsDrawer`) — laisse
  // l'utilisateur revenir directement sur la vue 3D, comme demandé.
  drawerPanel.addEventListener('click', (e) => {
    if (drawerHeader.contains(e.target as Node)) return;
    closeToolsDrawer();
  });

  const toolsToggleButton = createPillButton('🛠 Outils', {
    variant: 'soft',
    small: true,
    onClick: () => openToolsDrawer(),
  });

  /** Reconstruit la barre unique (plus de groupes séparés par un mode — voir
   * `isTrackReady`) : "Décor/Pièce" et "Tour joker" restent toujours visibles
   * mais désactivés tant que le tracé n'est pas prêt ; "Reprendre le tracé"
   * n'apparaît qu'une fois prêt (secours pour retoucher le tracé). Sur
   * mobile, ces boutons rejoignent le tiroir plutôt que la barre elle-même
   * (voir `isMobileToolbar`) — appelée aussi au redimensionnement de la
   * fenêtre pour basculer live entre les deux dispositions. */
  function renderToolbar(): void {
    toolbar.innerHTML = '';
    const ready = isTrackReady();

    setButtonDisabled(pieceOrDecorButton, !ready);
    setPillButtonLabel(jokerLapToolButton, jokerLapButtonLabel());
    setButtonDisabled(jokerLapToolButton, !ready);
    setPillButtonLabel(surfaceBrushButton, surfaceBrushLabel());
    setButtonDisabled(surfaceBrushButton, !ready);
    reopenTraceButton.hidden = !ready;

    const tools = [
      pieceOrDecorButton,
      jokerLapToolButton,
      surfaceBrushButton,
      reopenTraceButton,
      topViewButton,
      recenterButton,
      debugMarkersButton,
      decorSnapButton,
      positionToggleButton,
      helpWrap,
    ];

    closeToolsDrawer();
    if (isMobileToolbar()) {
      drawerPanel.append(drawerHeader, ...tools);
      toolbar.append(toolsToggleButton, saveButton);
    } else {
      toolbar.append(...tools, saveButton);
    }
  }
  renderToolbar();
  window.addEventListener('resize', renderToolbar);

  /**
   * En mode Tracer : légende des couleurs de la grille (aucun bouton — le
   * clic pose directement ce que la couleur de la case indique, voir
   * `computeTraceCandidates`/`handleTraceClick`). Une fois la boucle fermée,
   * bascule sur un simple bouton "Passer" tant que la ligne de départ
   * (5 cases) n'est pas terminée (voir `isDrawingStartLane`). Vide en mode
   * Personnaliser (rien à faire ici, le tracé est figé) ou une fois le
   * circuit et la ligne de départ tous les deux prêts.
   */
  function renderTraceTools(): void {
    traceToolsEl.innerHTML = '';
    if (isTrackReady()) {
      traceToolsEl.hidden = true;
      return;
    }
    traceToolsEl.hidden = false;

    if (isDrawingStartLane()) {
      const row = document.createElement('div');
      row.className = 'circuit-3d-editor-toolbar-group';
      const skipBtn = createPillButton('Passer (pas de ligne de départ)', {
        variant: 'soft',
        small: true,
        onClick: () => {
          draft = clearStartLane(draft);
          actionStatus = 'Ligne de départ ignorée.';
          actionIsError = false;
          scene?.refreshScene(draft);
          updateTraceHighlight();
          scene?.setGhostCorner(null);
          renderActionStatus();
          refreshValidation();
          updateInstructions();
          renderTraceTools();
        },
      });
      row.appendChild(skipBtn);
      traceToolsEl.appendChild(row);
      return;
    }

    if (draft.path.length === 0) return; // rien à légender avant le tout premier clic (départ)

    const legend = document.createElement('div');
    legend.className = 'circuit-3d-editor-trace-legend';
    const swatch = (color: number, label: string): string =>
      `<span class="circuit-3d-editor-trace-legend-item"><span class="circuit-3d-editor-trace-legend-swatch" style="background:#${color.toString(16).padStart(6, '0')}"></span>${label}</span>`;
    legend.innerHTML =
      swatch(ARM_COLORS[1], 'Tout droit / Petit') +
      swatch(ARM_COLORS[2], 'Moyen') +
      swatch(ARM_COLORS[3], 'Large') +
      swatch(HOVER_HIGHLIGHT_COLOR, 'Fermer la boucle');
    traceToolsEl.appendChild(legend);
  }
  renderTraceTools();

  /**
   * Retype chaque case du tracé principal (STRAIGHT/TURN). L'éditeur 3D n'a
   * pas de pinceau manuel STRAIGHT/TURN comme l'éditeur 2D
   * (`applyPathCellType`) : sans ce recalcul, `tryDrawPathCell` laisse TOUTE
   * case en `STRAIGHT` par défaut, ce qui ne casse pas le rendu 3D (indépendant
   * de ce champ, voir `computeTile`) mais casse silencieusement une vraie
   * règle de jeu — la division par deux du budget de déplacement en virage
   * (voir `shared/game/trackMovement.ts`) ne se déclenche jamais.
   *
   * Une case fait partie du virage si elle est couverte par une pièce groupée
   * `shape: 'corner'` (pivot ET tout son bras des deux côtés, voir
   * `coveredOffsets`) — PAS seulement sa case pivot géométrique : un virage
   * "Large" reste un seul virage pénible à négocier sur toute sa longueur
   * visuelle, pas un instant unique au milieu d'une ligne droite. Repli sur la
   * géométrie pure (direction entrante ≠ sortante) uniquement pour une case de
   * virage qui n'aurait pas pu se grouper (cas limite, voir
   * `computeCornerCandidate`) — un vrai virage à une seule case reste alors
   * correctement détecté. Ignore la case de départ (`START`) et, sur une
   * boucle fermée, la case de clôture dupliquée (dernière du tableau, hors de
   * `getCorePath`) — ni l'une ni l'autre n'est jamais consultée comme un
   * virage réel par la logique de course.
   */
  function deriveCellTypes(circuit: Circuit): TrackCell[] {
    const path = circuit.path;
    const core = getCorePath(path);
    const n = core.length;
    if (n < 2) return path;

    const turnIndices = new Set<number>();
    for (const piece of circuit.roadPieces ?? []) {
      if (piece.source !== 'path' || (piece.shape ?? 'straight') !== 'corner') continue;
      for (const o of coveredOffsets(piece)) {
        turnIndices.add((((piece.startIndex + o) % n) + n) % n);
      }
    }

    const retypedCore = core.map((cell, i) => {
      if (cell.type === TrackCellType.START) return cell;
      if (turnIndices.has(i)) return { ...cell, type: TrackCellType.TURN };
      const prev = core[(i - 1 + n) % n];
      const next = core[(i + 1) % n];
      const isTurn = cell.x - prev.x !== next.x - cell.x || cell.y - prev.y !== next.y - cell.y;
      return { ...cell, type: isTurn ? TrackCellType.TURN : TrackCellType.STRAIGHT };
    });
    return path.length > core.length ? [...retypedCore, path[path.length - 1]] : retypedCore;
  }

  async function persist(): Promise<void> {
    const finalized = finalizeEditedCircuit(draft);
    const validation = validateManualCircuitEdit(finalized);
    if (!validation.isValid) {
      actionStatus = validation.errors.join(' ');
      actionIsError = true;
      renderActionStatus();
      return;
    }
    setSaveButtonBusy(true);
    actionStatus = 'Enregistrement…';
    actionIsError = false;
    renderActionStatus();
    try {
      const retyped = { ...finalized, path: deriveCellTypes(finalized) };
      const segments = countPathSegmentTypes(retyped.path);
      const params = {
        ...saved.params,
        numStraights: segments.straights,
        numTurns: segments.turns,
        approximateCells: retyped.path.length,
      };
      const result =
        currentId == null
          ? await saveCircuitCopy(retyped, params)
          : await updateCircuit(currentId, retyped, params);
      currentId = result.id;
      draft = structuredClone(result.circuit);
      renderToolbar();
      renderTraceTools();
      scene?.refreshScene(draft);
      updateTraceHighlight();
      refreshValidation();
      updateInstructions();
      actionStatus = 'Circuit enregistré.';
      actionIsError = false;
      onSaved?.(result);
    } catch (err) {
      actionStatus = err instanceof Error ? err.message : 'Échec de l’enregistrement.';
      actionIsError = true;
    } finally {
      setSaveButtonBusy(false);
      renderActionStatus();
    }
  }

  return {
    dispose(): void {
      window.removeEventListener('resize', renderToolbar);
      scene?.dispose();
      scene = null;
      container.innerHTML = '';
    },
  };
}
